import type { RuleSet } from '../rules/schema';
import type { MatchConfig, PlayerId, SimState } from './types';

/**
 * `durationMinutes` (and `MatchConfig.duration`) for a match with no time
 * limit: it ends only by last player standing or when the host ends it
 * (the `endMatch` command, systems/victory.ts).
 */
const NO_TIME_LIMIT = 0;

/** Match lengths the host may pick, in minutes (`NO_TIME_LIMIT`: no limit). */
const MATCH_DURATIONS = [15, 30, 45, 60, NO_TIME_LIMIT] as const;
type MatchDuration = (typeof MATCH_DURATIONS)[number];

/** Map sizes the host may pick; `auto` sizes the map by player count. */
const MAP_SIZES = ['auto', 'small', 'medium', 'large', 'huge'] as const;
type MapSize = (typeof MAP_SIZES)[number];

/** Hex radius of each fixed map size. */
const MAP_SIZE_RADIUS: Record<Exclude<MapSize, 'auto'>, number> = {
  small: 22,
  medium: 28,
  large: 34,
  huge: 40,
};
/** `auto`: up to this many players get the smaller radius, more the larger. */
const AUTO_SMALL_PLAYERS = 3;
const AUTO_SMALL_RADIUS = 30;
const AUTO_LARGE_RADIUS = 35;

/** Bounds on a match's map radius and length (the snapshot validator enforces them). */
const MIN_MAP_RADIUS = 8;
const MAX_MAP_RADIUS = 40;
const MIN_MATCH_SECONDS = 60;
const MAX_MATCH_SECONDS = 3600;
/** Longest seed a host may type (it's sent to every peer). */
const MAX_SEED_LENGTH = 64;

/**
 * What the lobby host picks before a match. Every peer resolves the same
 * settings with the same roster and room into one `MatchConfig`.
 */
interface MatchSettings {
  durationMinutes: MatchDuration;
  mapSize: MapSize;
  /** The island's seed; empty means derived from the room id. */
  seed: string;
}

const DEFAULT_MATCH_SETTINGS: MatchSettings = {
  durationMinutes: 60,
  mapSize: 'auto',
  seed: '',
};

/** Hex radius of the map for `size` with `playerCount` players. */
function mapRadiusFor(size: MapSize, playerCount: number): number {
  if (size !== 'auto') return MAP_SIZE_RADIUS[size];
  return playerCount <= AUTO_SMALL_PLAYERS ? AUTO_SMALL_RADIUS : AUTO_LARGE_RADIUS;
}

/**
 * The seed the island and spawns use: the host's, else the room id (cut to
 * `MAX_SEED_LENGTH`, so it always passes `isMatchConfig`).
 */
function matchSeed(settings: MatchSettings, roomId: string): string {
  const seed = settings.seed.trim();
  return (seed.length > 0 ? seed : roomId).slice(0, MAX_SEED_LENGTH);
}

/** The match a lobby's settings make (what `createInitialState` stores). */
function resolveMatch(settings: MatchSettings, roomId: string, playerCount: number): MatchConfig {
  return {
    duration: settings.durationMinutes * 60,
    mapRadius: mapRadiusFor(settings.mapSize, playerCount),
    seed: matchSeed(settings, roomId),
  };
}

/** A well-formed `MatchSettings` (untrusted, e.g. from the lobby host). */
function isMatchSettings(value: unknown): value is MatchSettings {
  if (typeof value !== 'object' || value === null) return false;
  const v = value as Record<string, unknown>;
  return (
    (MATCH_DURATIONS as readonly unknown[]).includes(v.durationMinutes) &&
    (MAP_SIZES as readonly unknown[]).includes(v.mapSize) &&
    typeof v.seed === 'string' &&
    v.seed.length <= MAX_SEED_LENGTH
  );
}

/** A well-formed, in-bounds `SimState.match`. */
function isMatchConfig(value: unknown): value is MatchConfig {
  if (typeof value !== 'object' || value === null) return false;
  const v = value as Record<string, unknown>;
  return (
    (v.duration === NO_TIME_LIMIT ||
      (Number.isFinite(v.duration) &&
        (v.duration as number) >= MIN_MATCH_SECONDS &&
        (v.duration as number) <= MAX_MATCH_SECONDS)) &&
    Number.isSafeInteger(v.mapRadius) &&
    (v.mapRadius as number) >= MIN_MAP_RADIUS &&
    (v.mapRadius as number) <= MAX_MAP_RADIUS &&
    typeof v.seed === 'string' &&
    v.seed.length <= MAX_SEED_LENGTH
  );
}

/**
 * Seconds the match lasts: the host's pick, else the rules' default; null
 * when the host picked no time limit.
 */
function matchDuration(state: SimState, rules: RuleSet): number | null {
  const duration = state.match?.duration ?? rules.match.duration;
  return duration === NO_TIME_LIMIT ? null : duration;
}

/**
 * The player who may end the match early (`endMatch`): the lowest-sorted
 * player who hasn't resigned. The sim can't see who is connected, so this
 * stands in for phop's host (the lowest-sorted present roster peer): an
 * eliminated host still connected (spectating) stays host, and a resigned or
 * disconnected one (auto-resigned after 10 s) hands the role to the next.
 * Null once everyone has resigned.
 */
function matchHost(state: SimState): PlayerId | null {
  const present = Object.keys(state.players)
    .filter((id) => !state.players[id].resigned)
    .sort();
  return present[0] ?? null;
}

export type { MapSize, MatchDuration, MatchSettings };
export {
  DEFAULT_MATCH_SETTINGS,
  isMatchConfig,
  isMatchSettings,
  MAP_SIZE_RADIUS,
  MAP_SIZES,
  MATCH_DURATIONS,
  MAX_SEED_LENGTH,
  mapRadiusFor,
  matchDuration,
  matchHost,
  matchSeed,
  NO_TIME_LIMIT,
  resolveMatch,
};
